const MIN_SCALE = 0.2;
const READABLE_SCALE = 0.6;
const MAX_SCALE = 1.5;
const BUTTON_ZOOM_FACTOR = 1.25;
const PINCH_ZOOM_SENSITIVITY = 0.01;
const FIT_PADDING = 24;
const PAN_IGNORE_SELECTOR = '[data-flow-map-node], a, button, input, [role="menu"]';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default {
  FlowMap: {
    mounted() {
      this.viewport = this.el.closest('[data-flow-map-viewport]');
      this.root = this.el.closest('[data-flow-map]');
      this.view = { x: 0, y: 0, scale: 1 };
      this.fitMinScale = READABLE_SCALE;
      this.touched = false;
      this.drag = null;
      this.highlighted = null;
      this.size = this.canvasSize();

      this.onWheel = e => this.handleWheel(e);
      this.onPointerDown = e => this.handlePointerDown(e);
      this.onPointerMove = e => this.handlePointerMove(e);
      this.onPointerUp = e => this.handlePointerUp(e);
      this.onPointerOver = e => this.highlight(e.target.closest('[data-flow-map-node]')?.dataset.flowMapNode ?? null);
      this.onPointerLeave = () => this.highlight(null);
      this.onAction = e => this.handleAction(e);

      this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('pointerover', this.onPointerOver);
      this.viewport.addEventListener('pointerleave', this.onPointerLeave);
      this.root.addEventListener('click', this.onAction);

      this.observer = new ResizeObserver(() => (this.touched ? this.apply() : this.fit(this.fitMinScale)));
      this.observer.observe(this.viewport);

      this.fit(this.fitMinScale);
    },

    // LiveView patches reset the server-rendered style and data attributes, dropping the
    // transform and the highlight.
    updated() {
      const size = this.canvasSize();
      const resized = size.width !== this.size.width || size.height !== this.size.height;
      this.size = size;

      if (resized && !this.touched) {
        this.fit(this.fitMinScale);
      } else {
        this.apply();
      }

      this.showHighlight();
    },

    destroyed() {
      this.observer?.disconnect();
      this.viewport.removeEventListener('wheel', this.onWheel);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport.removeEventListener('pointerover', this.onPointerOver);
      this.viewport.removeEventListener('pointerleave', this.onPointerLeave);
      this.root.removeEventListener('click', this.onAction);
    },

    highlight(id) {
      if (id === this.highlighted) return;

      this.highlighted = id;
      this.showHighlight();
    },

    // Highlighted links move to the end so they draw over the lines they cross.
    showHighlight() {
      const edges = this.el.querySelector('[data-flow-map-edges]');
      const id = this.highlighted;
      edges.toggleAttribute('data-highlighting', id !== null);

      for (const edge of edges.querySelectorAll('[data-flow-map-edge]')) {
        const active = id !== null && (edge.dataset.from === id || edge.dataset.to === id);
        edge.toggleAttribute('data-active', active);
        if (active) edges.appendChild(edge);
      }
    },

    canvasSize() {
      return {
        width: Number(this.el.dataset.width),
        height: Number(this.el.dataset.height),
      };
    },

    apply() {
      const { x, y, scale } = this.view;
      this.el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    },

    // Maps too wide to fit at `minScale` are anchored left so the start of the journey stays in view.
    fit(minScale = MIN_SCALE) {
      const { width, height } = this.size;
      const { clientWidth, clientHeight } = this.viewport;
      if (!width || !height || !clientWidth || !clientHeight) return;

      const scale = clamp(
        Math.min((clientWidth - 2 * FIT_PADDING) / width, (clientHeight - 2 * FIT_PADDING) / height, 1),
        minScale,
        MAX_SCALE,
      );

      const contentWidth = width * scale;
      const x = contentWidth + 2 * FIT_PADDING <= clientWidth ? (clientWidth - contentWidth) / 2 : FIT_PADDING;

      this.view = { scale, x, y: FIT_PADDING };
      this.fitMinScale = minScale;
      this.touched = false;
      this.apply();
    },

    zoomAt(factor, originX, originY) {
      const { x, y, scale } = this.view;
      const nextScale = clamp(scale * factor, MIN_SCALE, MAX_SCALE);
      const ratio = nextScale / scale;

      this.view = {
        scale: nextScale,
        x: originX - (originX - x) * ratio,
        y: originY - (originY - y) * ratio,
      };
      this.touched = true;
      this.apply();
    },

    handleWheel(e) {
      e.preventDefault();

      if (e.ctrlKey || e.metaKey) {
        const rect = this.viewport.getBoundingClientRect();
        this.zoomAt(Math.exp(-e.deltaY * PINCH_ZOOM_SENSITIVITY), e.clientX - rect.left, e.clientY - rect.top);
        return;
      }

      this.view = { ...this.view, x: this.view.x - e.deltaX, y: this.view.y - e.deltaY };
      this.touched = true;
      this.apply();
    },

    handlePointerDown(e) {
      if (e.button !== 0 || e.target.closest(PAN_IGNORE_SELECTOR)) return;

      e.preventDefault();
      this.viewport.setPointerCapture(e.pointerId);
      this.drag = { pointerId: e.pointerId, x: e.clientX - this.view.x, y: e.clientY - this.view.y };
    },

    handlePointerMove(e) {
      if (!this.drag || e.pointerId !== this.drag.pointerId) return;

      this.view = { ...this.view, x: e.clientX - this.drag.x, y: e.clientY - this.drag.y };
      this.touched = true;
      this.apply();
    },

    handlePointerUp(e) {
      if (!this.drag || e.pointerId !== this.drag.pointerId) return;

      this.viewport.releasePointerCapture(e.pointerId);
      this.drag = null;
    },

    handleAction(e) {
      const action = e.target.closest('[data-flow-map-action]')?.dataset.flowMapAction;
      if (!action) return;

      const { clientWidth, clientHeight } = this.viewport;

      if (action === 'zoom-in') this.zoomAt(BUTTON_ZOOM_FACTOR, clientWidth / 2, clientHeight / 2);
      if (action === 'zoom-out') this.zoomAt(1 / BUTTON_ZOOM_FACTOR, clientWidth / 2, clientHeight / 2);
      if (action === 'fit') this.fit();
    },
  },
};
